import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import DeleteModal from "components/Common/DeleteModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import { filters, giftCardTypes } from "components/Common/constants"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Switch from "components/Shared/Switch"
import TableShimmer from "components/Shared/TableShimmer"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
} from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"

import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import {
  deleteGiftCard,
  getAllGiftsCards,
  updateGiftCardStatus,
} from "store/actions/giftCardAction"
import formatDateTime from "components/Shared/formatDateTime"

const GiftCard = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Eidiyah | Namq")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  // const minDate = searchParams.get("minDate") || ""
  // const maxDate = searchParams.get("maxDate") || ""

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    giftCards,
  } = useSelector(state => state.GiftCard)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      37,
      "gift_card_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      37,
      "gift_card_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      37,
      "gift_card_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      37,
      "gift_card_4",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    } else {
      fetchAllRecords()
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  const updatedFilters = [...filters, giftCardTypes]

  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({})
  const [selectedFilters, setSelectedFilters] = useState({
    status: 2,
    sortOrder: 1,
    type: 0,
  })

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const fetchAllRecords = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: 2,
      orderBy: 1,
      keyword: "",
      type: 0,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllGiftsCards(payload))
    setIsApiCalled(true)
  }

  const getApiCall = (page = 1) => {
    const { status, sortOrder, type } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      type,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    return dispatch(getAllGiftsCards(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/eidiyah?page=${page}`)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handleSearch = () => {
    getApiCall()
  }

  const onPageChange = page => {
    getApiCall(page)
  }

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
      type: 0,
      dateRange: {},
    }
    dispatch(getAllGiftsCards(payload))
    navigate(`/eidiyah?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
      type: 0,
    })
    setDateRange({})
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.clear()
    }
  }

  const handleStatusUpdate = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    dispatch(updateGiftCardStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteGiftCard(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Eidiyah List")}
                isAddBtn={loading ? false : usersPermissions.create}
                addBtnText={t("Add Eidiyah")}
                onAddBtnClick={() => navigate("/eidiyah/add")}
              />
              <CardBody>
                <Filters
                  filters={updatedFilters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  searchBy={t("Name")}
                  colSize={4}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                />
                <div className="">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : giftCards.length === 0 ? (
                    <p className="text-center my-3">{t("No data found")}</p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ maxWidth: 50 }}>{t("S. No.")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Total Purchased")}</th>
                          <th>{t("Amount")}</th>
                          {/* <th>{t("Starts At")}</th>
                          <th>{t("Expires At")}</th> */}
                          <th>{t("Created By")}</th>
                          <th>{t("Status")}</th>
                          <th className="text-end">
                            <span
                              className="d-inline-block "
                              style={{
                                paddingRight: 30,
                                display: "block",
                              }}
                            >
                              {t("Action")}
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {giftCards.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              navigate(`/eidiyah/details?id=${item._id}`)
                            }}
                            className="cursor-pointer"
                          >
                            <th scope="row">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>
                            <td>
                              {language === "sa"
                                ? item.nameAr
                                : item.name || "-"}
                            </td>
                            <td>{item.totalPurchased ?? "-"}</td>
                            <td>
                              {item?.amount ?? "-"} {item?.currency }
                            </td>
                            {/* <td>{formatDateTime(item?.startsAt)}</td>
                            <td>{formatDateTime(item?.expiresAt)}</td> */}
                            <td>{item?.createdBy?.username || "-"}</td>
                            <td>
                              {item.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                            <td className="text-end">
                              <div className="d-flex align-items-center justify-content-end gap-2">
                                {usersPermissions.edit && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      handleStatusUpdate(item._id, item.status)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isLoading
                                    }
                                  />
                                )}
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    tag="a"
                                    className="card-drop"
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <i className="mdi mdi-dots-vertical font-size-18"></i>
                                  </DropdownToggle>
                                  <DropdownMenu className="dropdown-menu-end">
                                    {usersPermissions.edit && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          navigate(
                                            `/eidiyah/edit?id=${item._id}`
                                          )
                                        }}
                                      >
                                        <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                        {t("Edit")}
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={e => {
                                        e.stopPropagation()
                                        navigate(
                                          `/eidiyah/details?id=${item._id}`
                                        )
                                      }}
                                    >
                                      <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                      {t("View")}
                                    </DropdownItem>
                                    {usersPermissions.delete && (
                                      <DropdownItem
                                        onClick={e => {
                                          e.stopPropagation()
                                          setItemIdToDelete(item._id)
                                          setShowDeleteModal(true)
                                        }}
                                      >
                                        <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                        {t("Delete")}
                                      </DropdownItem>
                                    )}
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && giftCards.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={giftCards}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Eidiyah"),
        })}
      />
    </div>
  )
}

export default GiftCard
